import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  alertVisibilitySchema,
  type AlertVisibility,
} from '@kirin/api-client/contracts/alerts';

import {
  ALERTS_VISIBILITY_URL_PARAM,
  readAlertVisibilityFromStorage,
} from '@/lib/alertVisibilityStorage';

export interface UseAlertVisibilityResult {
  /**
   * Currently selected visibility. Bind this to
   * `<AlertVisibilitySelector value={...} />` and the corresponding setter to
   * its `onChange`, and to the request param + React Query key.
   */
  visibility: AlertVisibility;
  setVisibility: (next: AlertVisibility) => void;
}

/**
 * Reads the optional `?visibility=` URL override. Invalid values fall
 * through to the stored preference rather than corrupting state.
 */
function readUrlVisibilityOverride(
  searchParams: URLSearchParams
): AlertVisibility | null {
  const raw = searchParams.get(ALERTS_VISIBILITY_URL_PARAM);
  if (raw === null) return null;
  const parsed = alertVisibilitySchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Centralised state machine for the alerts toolbar visibility selector
 * (KIR-2512, KIR-2606 deep-link override).
 *
 * - Initial value: `?visibility=` URL param if valid, else
 *   `localStorage` (legacy migration in `readAlertVisibilityFromStorage`).
 * - URL override is stripped on the next tick (`replace: true`) and is
 *   NOT persisted to `localStorage` — the user's saved preference is only
 *   updated by the selector's `userInteractedRef` write path. Notification
 *   deep links (Go agent / IDE extension) carry `?visibility=all` so
 *   suppressed alerts land on the All view.
 */
export function useAlertVisibility(scopeKey: string): UseAlertVisibilityResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Lazy: URL override wins over storage on first render only.
  const [visibility, setVisibility] = useState<AlertVisibility>(
    () =>
      readUrlVisibilityOverride(searchParams) ??
      readAlertVisibilityFromStorage(scopeKey)
  );

  // Strip the `?visibility=` param post-render so subsequent toggles
  // persist normally and copy/paste does not pin the override. Idempotent
  // — once gone, further runs hit the early return.
  useEffect(() => {
    if (searchParams.get(ALERTS_VISIBILITY_URL_PARAM) === null) return;
    const next = new URLSearchParams(searchParams);
    next.delete(ALERTS_VISIBILITY_URL_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return { visibility, setVisibility };
}
